import { Form } from 'react-final-form';
import { Stack, Unstable_Grid2 as Grid, InputAdornment } from '@mui/material';

import { Button, Divider, Typography } from '@occc/ui-design-system';
import { TextField, Select, SelectAutocomplete } from '@occc-app/design-system';
import { makeZodValidate, DatetimeRangeFields } from '@occc/ui-forms';

import {
  ConnectionDurationOperatorEnum,
  ConnectionHistoryFilterSchema,
} from './connection-history-filter.schema';
import {
  CONNECTIVITY_STATUS_OPTIONS,
  DISCONNECT_REASON_OPTIONS,
} from './connection-history-filter.constants';
import { ConnectionHistoryFilterFormValues } from './connection-history-filter.types';

const validateFilterForm = makeZodValidate(ConnectionHistoryFilterSchema);

export type ConnectionHistoryFilterContainerProps = {
  value?: ConnectionHistoryFilterFormValues;
  onChange?: (value: ConnectionHistoryFilterFormValues) => void;
};

export const ConnectionHistoryFilterContainer = (props: ConnectionHistoryFilterContainerProps) => {
  const { onChange, value } = props;
  return (
    <Stack gap={3} p={3}>
      <Typography variant='h6'>Filter Connection History</Typography>
      <Form<ConnectionHistoryFilterFormValues>
        initialValues={value}
        onSubmit={(values) => {
          onChange?.(values);
        }}
        validate={validateFilterForm}
        render={(props) => {
          const onReset = () => {
            // Reset form to empty state! Not to initial values
            props.form.reset({});
            props.form.submit();
          };
          return (
            <form onSubmit={props.handleSubmit}>
              <Grid container spacing={3}>
                <Grid xs={12}>
                  <Select
                    name={
                      'connectionStatusEquals' satisfies keyof ConnectionHistoryFilterFormValues
                    }
                    label='Connectivity Status'
                    data={CONNECTIVITY_STATUS_OPTIONS}
                    formControlProps={{ size: 'medium', margin: 'none' }}
                  />
                </Grid>

                <DatetimeRangeFields<keyof ConnectionHistoryFilterFormValues>
                  modeFieldName='lastConnectedAt'
                  modeFieldLabel='Connected At'
                  modeFieldValue={props.values.lastConnectedAt}
                  fromFieldName={'lastConnectDateFrom'}
                  toFieldName={'lastConnectDateTo'}
                />
                <Grid xs={12}>
                  <Divider />
                </Grid>

                <DatetimeRangeFields<keyof ConnectionHistoryFilterFormValues>
                  modeFieldName='lastDisconnectedAt'
                  modeFieldLabel='Disconnected At'
                  modeFieldValue={props.values.lastDisconnectedAt}
                  fromFieldName={'lastDisconnectDateFrom'}
                  toFieldName={'lastDisconnectDateTo'}
                />
                <Grid xs={12}>
                  <Divider />
                </Grid>

                <Grid xs={6}>
                  <Select
                    name={
                      'connectionDurationOperator' satisfies keyof ConnectionHistoryFilterFormValues
                    }
                    label={'Connection Duration'}
                    data={[
                      { value: ConnectionDurationOperatorEnum.enum.gte, label: 'More Than' },
                      { value: ConnectionDurationOperatorEnum.enum.lte, label: 'Less Than' },
                    ]}
                  />
                </Grid>
                <Grid xs={6}>
                  <TextField
                    fieldProps={{
                      parse: (value) => (typeof value === 'string' ? parseInt(value) : undefined),
                    }}
                    name={
                      'connectionDurationSeconds' satisfies keyof ConnectionHistoryFilterFormValues
                    }
                    type='number'
                    label={'Time'}
                    InputProps={{
                      endAdornment: <InputAdornment position='end'>seconds</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid xs={12}>
                  <SelectAutocomplete<keyof ConnectionHistoryFilterFormValues>
                    name='disconnectActionContains'
                    label='Disconnect Reason'
                    placeholder='Start typing to select disconnect reason'
                    options={DISCONNECT_REASON_OPTIONS}
                  />
                </Grid>
                <Grid xs={12}>
                  <Stack direction='row' spacing={2} justifyContent='flex-end'>
                    <Button variant='text' onClick={onReset}>
                      Reset
                    </Button>

                    <Button
                      variant='contained'
                      type='submit'
                      disabled={props.hasValidationErrors || props.pristine}
                    >
                      Apply
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </form>
          );
        }}
      />
    </Stack>
  );
};
